import { isString, isFunction, isObjectLike, isNumber } from 'lodash';
import { decode } from 'base-64';
import { ConfigsService, WindowProxy } from '@sm360/widget-commons-core-services';
import Logger from '@sm360/logger-sentry';

/**
 * Regroup some utility functions
 */
class UtilsService {
    /**
     * Return value to the closest rounded step if the value is between min and max.
     * Typically used in trade-in slider.
     *
     * @memberof UtilsService
     * @param {Number} value - The value to round
     * @param {Number} [step=1] - The step where we want to increment ou decrement value
     * @param {Number} [min] - Minimum inclusive value where we want to stop
     * @param {Number} [max] - Maximum inclusive value where we want to stop
     * @return {Number} The value rounded to the step
     *
     * @example
     * UtilsService.roundValueToStepIntoRange(117, 25); // 125
     * UtilsService.roundValueToStepIntoRange(112, 25); // 100
     * UtilsService.roundValueToStepIntoRange(112, 25, 102); // 102
     * UtilsService.roundValueToStepIntoRange(117, 25, null, 123); // 123
     */
    roundValueToStepIntoRange(value, step = 1, min, max) {
        if (!isNumber(value) || !isNumber(step) || (min && !isNumber(min)) || (max && !isNumber(max))) {
            const logger = new Logger('UtilsService', ConfigsService.getLoggerContext());
            logger.error('roundValueToStepIntoRange error: parameter must be a number.');
            return null;
        }
        const rounded = Math.round(value / step) * step;
        if (min && (rounded <= min || value === min)) {
            return min;
        } else if (max && (rounded >= max || value === max)) {
            return max;
        }
        return rounded;
    }

    /**
     * Give the component DisplayName from a component
     * @memberof UtilsService
     * @param {ReactComponent} Component - A React component
     * @return {string} The value of the Display Name
     */
    getComponentDisplayName(Component) {
        if (isFunction(Component) || isObjectLike(Component)) {
            return Component.displayName || Component.name || 'Component';
        }
        return '';
    }

    /**
     * Split by the space a string to have a Firstname and a Lastname
     * Return only a lastname if there's no space
     * @memberof UtilsService
     * @param {string} name - A name (with or without a space)
     * @return {{firstname: ?string, lastname: string}} The name object
     */
    splitName(name) {
        if (!isString(name)) {
            return null;
        }

        let firstname = null;
        let lastname = null;

        if (name.indexOf(' ') === -1) {
            lastname = name;
        } else {
            const splitName = name.split(' ');
            firstname = splitName.shift();
            lastname = splitName.join(' ');
        }

        return { firstname, lastname };
    }

    /**
     * Decode and parse a string to have the token information
     * @memberof UtilsService
     * @param {string} token - The encode string token
     * @return {Object} The token object
     */
    extractTokenPayload(token) {
        if (
            !isString(token) ||
            token.indexOf('.') === token.lastIndexOf('.') ||
            token.indexOf('.') === -1 ||
            token.lastIndexOf('.') === -1 ||
            !WindowProxy.get()
        ) {
            return null;
        }

        const tokenPayloadString = token.substring(token.indexOf('.') + 1, token.lastIndexOf('.'));

        return JSON.parse(this.decodeBase64(tokenPayloadString));
    }

    /**
     * Decode a string encoded in Base64
     * Wraps the {@link base64.decode} function for retro-compatibility
     * @memberof UtilsService
     * @param {string} encodedString - The base64 encoded string
     * @return {string} The decoded string or an empty string when {@link window} is not available
     */
    decodeBase64(encodedString) {
        return decode(encodedString);
    }

    /**
     * Returns the integer value of the argument, null otherwise
     * @memberof UtilsService
     * @param {?any} value the argument
     * @return {?number} the argument as an integer or null
     */
    getIntegerOrNull(value) {
        return parseInt(value, 10) || null;
    }
}

export default new UtilsService();
